/**
 * 再現エンジン（ocg）を `SourcePort` にする（索引 Issue #498 の段階 8）。**`duelEditor/timeline/` の外**。
 *
 * これが「コアはドメインを知らない」の答え合わせ: ocg 側は**何も変えずに**、
 *   イベント列 → アンカー（時刻を持つ点）
 *   型の仕様 → **外から渡す**（テーマの演出から作る。`editorPorts/duelEffects.js::duelSpecs`）
 *   整列 → 戦略 `sequential`（前の帯を避けて並ぶ）
 *   盤面・HUD → フレームごとの派生（`context`）と状態チャンネル
 * の 4 つに写すだけでコアに載る。
 */
import { SourcePort } from '../duelEditor/timeline/ports/source';
import { resolveAnchors } from '../duelEditor/timeline/align';
import { commitRows } from '../duelEditor/timeline/states';
import { DEFAULT_FPS } from '../duelEditor/timeline/time';
import { eventKeys, isStartKey, opBySeq, opVideoMs } from '../components/ocgCore';
import { boardAt, buildBoardTimeline } from './boardTimeline';
import { buildHudTimeline, hudStateAt, DEFAULT_START_LP } from './hudState';

export const OCG_SOURCE_ID = 'ocg';

/**
 * イベント列 → アンカー。
 *
 * - `ms` は**答えた時刻**（`op.video_ms`。初手は replay の開始位置）。事実なので編集では書き換えない
 * - `ordering` は「並びの基準になるか」（core のイベントだけ。問い合わせ・ヒントは後ろを押さない）
 * - `groupId` は応答（`op_uuid`）。同じ応答のイベントは、人が 1 つ動かすと続きとして付いてくる
 * - `event` をそのまま載せるのは、**演出ファイルの `opens(row)` が `row.event` を読む**ため（形を変えない）
 */
export function ocgAnchors({ events = [], ops = [], startMs = 0 } = {}) {
  const keys = eventKeys(events, ops);
  const bySeq = opBySeq(ops);
  return events.map((event, i) => {
    const key = keys[i];
    const op = key && !isStartKey(key) && event.op_seq != null ? bySeq.get(event.op_seq) : null;
    return {
      key,
      index: i,
      ms: key && isStartKey(key) ? startMs : opVideoMs(op),
      ordering: event.kind === 'event',
      groupId: op?.op_uuid ?? null,
      event,
      payload: event,
    };
  });
}


/**
 * 盤面と HUD の**状態チャンネル**（索引 Issue #498 の段階 9）。
 *
 * どちらも「アンカーの時刻で畳んで、フレームごとに引く」もの＝状態そのものなので、帯ではなくチャンネルにする。
 * 畳み方は既存の実装（`buildBoardTimeline` / `buildHudTimeline`）をそのまま使う（**演出側は無変更**）。
 *
 * **盤面はイベントの後の状態へ進む時刻を演出が決められる**（Issue #566）: 帯が `commitMs`（帯の頭から何 ms。
 * `duelEditor` の演出の口 `board`）を宣言していれば、そのイベントの行の時刻を差し替えてから畳む（`commitRows`）。
 * 前のイベントより前には進まない（`max(自分, 前)`）のは `buildBoardTimeline` が並びどおりに畳むときに付ける。
 * **HUD は対象外**（アンカーの時刻のまま）。宣言が無ければ**帯の頭**で進む（Issue #583。整列はアンカーの時刻を
 * フレームに丸めるが帯の頭は丸めないので、アンカーの時刻のままだと帯が始まる前の 1 フレームだけ盤面が先に進んでいた）。
 */
export function ocgStateChannels({ boardSteps = null, startLp = DEFAULT_START_LP, firstPlayer = 0 } = {}) {
  return [
    {
      id: 'board',
      label: '盤面',
      kind: 'build',
      build: (anchors, _ctx, objects) => buildBoardTimeline(boardSteps, commitRows(anchors, objects)),
      at: (ms, built) => boardAt(ms, built),
    },
    {
      id: 'hud',
      label: 'HUD（LP・ターン・フェイズ）',
      kind: 'build',
      build: (anchors) => buildHudTimeline(anchors, { startLp, firstPlayer }),
      at: (ms, built) => hudStateAt(ms, built),
    },
  ];
}

/**
 * 演出ファイルの `build` に渡す材料。**整列前の時刻**（答えた時刻を単調にしただけ）で盤面・HUD を作る
 * —— 生成のときはまだ整列していない。
 */
export function buildContext({ anchors = [], boardSteps = null, startLp = DEFAULT_START_LP, firstPlayer = 0, players = [], ops = [], confirmedUids = null, fps = DEFAULT_FPS } = {}) {
  const base = resolveAnchors({ anchors, objects: [], strategy: 'fixed', fps }).rows;
  const board = buildBoardTimeline(boardSteps, base);
  const hud = buildHudTimeline(base, { startLp, firstPlayer });
  return {
    sourceId: OCG_SOURCE_ID,
    rows: base,
    board,
    boardAt: (ms) => boardAt(ms, board),
    hud,
    players,
    ops,
    confirmedUids: confirmedUids ?? new Set(),
  };
}

/**
 * @param events `OcgGetState` の events
 * @param ops replay の操作列（答えた時刻）
 * @param startMs replay の開始位置
 * @param boardSteps `OcgGetState` の board_steps
 */
export function createOcgSource({
  events = [], ops = [], startMs = 0, startLp = DEFAULT_START_LP, firstPlayer = 0,
  boardSteps = null, players = [], confirmedUids = null, fps = DEFAULT_FPS,
  specs = [], channels: extraChannels = [],
} = {}) {
  const anchors = ocgAnchors({ events, ops, startMs });
  // **型の仕様は外から渡す**（Issue #534 / #639。テーマの演出から作る＝ `duelSpecs`）
  const ctx = buildContext({ anchors, boardSteps, startLp, firstPlayer, players, ops, confirmedUids, fps });
  // **チャンネルも足せる**（演出が置いたものの生存＝ `fxObjectsChannel`。Issue #534）
  const channels = [...ocgStateChannels({ boardSteps, startLp, firstPlayer }), ...(extraChannels ?? [])];
  const port = SourcePort.create({
    id: () => OCG_SOURCE_ID,
    anchors: () => anchors,
    specs: () => specs,
    // 演出の帯は前の帯を避けて並ぶ（コアの `sequential`）
    alignStrategy: () => 'sequential',
    // 盤面と HUD は**状態**（帯ではない）
    states: () => channels,
  });
  return { ...port, ctx };
}
